import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useApp } from '@/app/AppContext'
import { NAVIGATION } from '@/app/navigation'
import { fetchDuplicates, hasSearchableCriteria, type DuplicateMatch } from './counterpartyApi'

/**
 * Поиск дублей контрагента **до** сохранения (тикет #70, ТЗ §4.2).
 *
 * ## Почему до, а не после
 *
 * Дубль, найденный после сохранения, уже успел собрать на себя сделку, счёт и звонок;
 * слияние тогда — перенос истории, а не отмена опечатки. Показанный до, он стоит одного
 * нажатия «открыть существующую».
 *
 * ## Почему подсказка, а не запрет
 *
 * Совпадение телефона — обычное дело у группы компаний с общей приёмной, и отказ по нему
 * остановил бы настоящую работу. Филиал с тем же ИНН и другим КПП — вообще законная вторая
 * карточка: уникальность контрагента по паре `(inn, kpp)`, а не по ИНН.
 *
 * Единственное, что действительно не сохранится, — точное совпадение пары. Его сервер
 * помечает флагом `blocking`, и только оно показывается как запрет. Смешать эти два случая
 * значит либо пугать там, где всё в порядке, либо не предупредить там, где сохранение
 * сейчас упадёт.
 */

interface DuplicatePanelProps {
  /** Значения формы новой карточки: по ним и ищем. */
  criteria: { inn?: string; kpp?: string; phone?: string; email?: string }
  /**
   * Сообщает форме, что сохранять нельзя.
   *
   * Флаг поднимает **сервер** (`blocking`), а панель только передаёт: своё правило «когда
   * нельзя сохранять» на клиенте разошлось бы с серверным, и кнопка осталась бы активной
   * под красным предупреждением — то есть предупреждение стало бы декорацией.
   */
  onBlockingChange?: (blocking: boolean) => void
  /** Путь к карточке найденного контрагента; берётся у вызывающего ради тестируемости. */
  cardPathOf?: (id: string) => string
  /**
   * Карточка, на которой панель открыта (#99): она совпадает сама с собой по каждому
   * признаку, и в собственных дублях её быть не должно.
   */
  excludeId?: string
  /** Действие у найденного — на карточке это «слить в эту карточку» (#99). */
  renderAction?: (match: DuplicateMatch) => React.ReactNode
  /**
   * `form` — разметка окна заведения (`.dup` из CreateRecord.dc.html), на две колонки
   * `form-grid`. Тексты, роли и правило блокировки те же: меняется только вид.
   */
  variant?: 'panel' | 'form'
}

export function DuplicatePanel({
  criteria,
  onBlockingChange,
  cardPathOf = defaultCardPath,
  excludeId,
  renderAction,
  variant = 'panel',
}: DuplicatePanelProps) {
  const { t } = useApp()

  // Ввод отпускается на 400 мс: запрос на каждое нажатие клавиши в поле ИНН — это десять
  // запросов на один номер, из которых нужен последний. Задержка не про экономию, а про
  // то, что список дублей, мигающий при наборе, читать невозможно.
  const debounced = useDebounced(criteria, 400)
  const searchable = hasSearchableCriteria(debounced)

  const query = useQuery({
    queryKey: ['counterparty-duplicates', debounced],
    enabled: searchable,
    queryFn: ({ signal }) => fetchDuplicates(debounced, signal),
    retry: false,
  })

  // Флаг снимается ВСЕГДА, когда блокирующего совпадения нет, — включая случаи «поиск
  // упал» и «признаки стёрли». Иначе однажды поднятый флаг переживает исправление ИНН, и
  // кнопка остаётся мёртвой без единого объяснения на экране.
  // On a card (excludeId) the exact INN/KPP match is the card itself: "cannot save" there
  // would be a red warning about a form that does not exist.
  const blockingNow = !excludeId && Boolean(query.data?.blocking) && searchable
  useEffect(() => {
    onBlockingChange?.(blockingNow)
  }, [blockingNow, onBlockingChange])

  // Пока ни один признак не заполнен, панель молчит. Написать здесь «дублей не найдено»
  // значило бы выдать «не искали» за «искали и не нашли» — то есть разрешить сохранить
  // дубль с чистой совестью.
  if (!searchable) return null
  if (variant === 'form') {
    return <FormVariant query={query} excludeId={excludeId} blocking={blockingNow} cardPathOf={cardPathOf} />
  }
  if (query.isLoading) return <p className="muted">{t('card.cp.dup.searching')}</p>

  // Отказ поиска — не «дублей нет». Молчание здесь означало бы, что сбой сети выглядит
  // как подтверждение уникальности.
  if (query.error) {
    return (
      <p className="notice" role="alert">
        {t('card.cp.dup.failed')}
      </p>
    )
  }

  const matches = (query.data?.matches ?? []).filter((match) => match.id !== excludeId)
  if (matches.length === 0) return <p className="muted">{t('card.cp.dup.none')}</p>

  const blocking = blockingNow

  return (
    <section
      className={blocking ? 'notice notice-error' : 'notice'}
      role={blocking ? 'alert' : 'status'}
    >
      <p>{blocking ? t('card.cp.dup.blocking') : t('card.cp.dup.found', { count: matches.length })}</p>

      <ul className="card-duplicates">
        {matches.map((match) => (
          <li key={match.id}>
            <Link to={cardPathOf(match.id)}>{match.name}</Link>
            <span className="muted">
              {' '}
              {t('column.inn')}: {match.inn ?? '—'}
              {/* КПП показывается всегда, даже пустой: именно он отличает филиал от
                  дубля, и его отсутствие на экране превращает «второй филиал» в
                  «та же организация дважды». */}
              {' · '}
              {t('column.kpp')}: {match.kpp ?? '—'}
            </span>
            <span className="badges">
              {match.matchedOn.map((reason) => (
                <span key={reason} className="badge">
                  {t(`card.cp.dup.by.${reason}`)}
                </span>
              ))}
            </span>
            {match.blocked && <span className="badge badge-warn">{t('card.cp.dup.blocked')}</span>}
            {renderAction?.(match)}
          </li>
        ))}
      </ul>
    </section>
  )
}

/** Вид для окна заведения: `.dup` с иконкой, причиной и «Открыть». */
function FormVariant({ query, excludeId, blocking, cardPathOf }: {
  query: { isLoading: boolean; error: unknown; data?: { matches: DuplicateMatch[] } }
  excludeId?: string
  blocking: boolean
  cardPathOf: (id: string) => string
}) {
  const { t } = useApp()
  if (query.isLoading) return <span className="help span-2">{t('card.cp.dup.searching')}</span>
  if (query.error) {
    return (
      <div className="alert alert-warning span-2" role="alert">
        <span className="fg">{t('card.cp.dup.failed')}</span>
      </div>
    )
  }
  const matches = (query.data?.matches ?? []).filter((match) => match.id !== excludeId)
  if (matches.length === 0) {
    return <span className="flag flag-success span-2">{t('card.cp.dup.none')}</span>
  }
  return (
    <div className="dup span-2" role={blocking ? 'alert' : 'status'}>
      <svg className="i ico" viewBox="0 0 16 16" aria-hidden="true" style={{ justifySelf: 'center', alignSelf: 'start', marginTop: 2 }}>
        <path d="M8 2L1.5 13.5h13zM8 6.5v3M8 11.5v.5" />
      </svg>
      <div className="stack" style={{ gap: 6 }}>
        <span className="strong">
          {blocking ? t('card.cp.dup.blocking') : t('card.cp.dup.found', { count: matches.length })}
        </span>
        <ul className="dup-list">
          {matches.map((match) => (
            <li key={match.id}>
              <Link to={cardPathOf(match.id)}>{match.name}</Link>
              <span className="t-sm secondary mono">
                {t('column.inn')} {match.inn ?? '—'} · {t('column.kpp')} {match.kpp ?? '—'}
              </span>
              {match.matchedOn.map((reason) => (
                <span key={reason} className="badge">{t(`card.cp.dup.by.${reason}`)}</span>
              ))}
              {match.blocked && <span className="badge b-warning">{t('card.cp.dup.blocked')}</span>}
              <Link to={cardPathOf(match.id)} className="btn btn-secondary btn-sm">{t('create.dup.open')}</Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

/**
 * Блокирует ли найденное сохранение.
 *
 * Отдельной функцией, потому что решение принимает форма (кнопка «Сохранить»), а панель
 * только показывает. Разнесённое по двум местам условие «когда нельзя сохранять» разошлось
 * бы, и кнопка осталась бы активной под красным предупреждением.
 */
export function blocksSaving(duplicates: { blocking: boolean } | undefined): boolean {
  return duplicates?.blocking ?? false
}

/**
 * Путь карточки найденного контрагента.
 *
 * Берётся из навигации, а не собирается из кода сущности: карточка живёт под путём своего
 * списка (`/counterparties/:id`), и это решение роутера (#66), а не свойство имени
 * сущности. Ссылка, собранная из `sales.counterparty` по правилу, выглядела бы рабочей
 * ровно до нажатия — а нажимают на неё именно тогда, когда торопятся не завести дубль.
 */
function defaultCardPath(id: string): string {
  const item = NAVIGATION.flatMap((section) => section.items).find(
    (candidate) => candidate.listEntity === 'sales.counterparty',
  )
  return item ? `${item.path}/${id}` : `/${id}`
}

/**
 * Значение, отстающее от источника на заданную задержку.
 *
 * Свой хук, а не библиотека: пять строк против зависимости, и поведение при размонтировании
 * здесь важнее универсальности — таймер обязан сниматься, иначе setState прилетит в
 * компонент, которого уже нет.
 */
function useDebounced<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs)
    return () => clearTimeout(timer)
    // Сравнение по значению, а не по ссылке: объект критериев пересоздаётся на каждый
    // рендер формы, и зависимость по нему перезапускала бы таймер вечно.
  }, [JSON.stringify(value), delayMs])

  return settled
}

export type { DuplicateMatch }
